import { createStyles, makeStyles } from "@material-ui/core";
import { scrollToElementById } from "../../modules";
import { FieldArray, useField } from "formik";
import * as React from "react";

import {
    baseColors,
    Button,
    ExpansionPanels,
    PanelProps,
    RelodeTheme,
    SetFieldValue,
} from "..";
import { PlusIcon } from "../../assets/icons";
import {
    CustomTextJobQualification,
    CustomYesNoJobQualification,
    EducationJobQualification,
    ICustomJobQualification,
    ILicenseJobQualification,
    JobQualifications,
    LicenseAnyOfJobQualification,
    LicenseJobQualification,
    Qualification,
    SkillJobQualification,
    YearsExperience,
} from "../../domain";
import {
    customTextPanels,
    customYesNoPanels,
    educationPanels,
    licenseAllOfPanels,
    licenseAnyOfPanels,
    skillAllOfPanels,
    skillAnyOfPanels
} from "./panels";

const useStyles = makeStyles((theme: RelodeTheme) =>
    createStyles({
        background: {
            backgroundColor: baseColors.cool_gray,
        },
        addQualsContainer: {
            margin: theme.spacing(3, 0, 3, 0),
            padding: theme.spacing(2),
            borderRadius: theme.spacing(0.5),
            border: "1px dashed" + baseColors.gray,
            background: theme.palette.background.default,
            textAlign: "center",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 16,
        },
        addQualsContainerRow: {
            display: "flex",
            gap: 10,
            alignSelf: "center",
        },
    })
);

const usePlaceHolderId = () => {
    const placeHolderId = React.useRef(
        relodeConstants.QUALIFICATION_PLACEHOLDER_ID_START
    );
    const getPlaceholderIndex = () => {
        placeHolderId.current++;
        return placeHolderId.current;
    };

    return [getPlaceholderIndex];
};

interface QualificationEditorProps {
    fieldName: string;
    jobQualifications: {
        educationJobQualifications: EducationJobQualification[];
        skillJobQualifications: SkillJobQualification[];
        skillAnyOfJobQualifications: SkillJobQualification[];
        licenseJobQualifications: LicenseJobQualification[];
        licenseAnyOfJobQualifications: LicenseAnyOfJobQualification[];
        customYesNoJobQualifications: CustomYesNoJobQualification[];
        customTextJobQualifications: CustomTextJobQualification[];
    };
    setFieldValue: SetFieldValue;
}

export const JobQualificationsEditor = (props: QualificationEditorProps) => {
    const classes = useStyles({});
    const [getPlaceholderIndex] = usePlaceHolderId();

    const {
        fieldName,
        jobQualifications: {
            educationJobQualifications,
            skillJobQualifications,
            skillAnyOfJobQualifications,
            licenseJobQualifications,
            licenseAnyOfJobQualifications,
            customYesNoJobQualifications,
            customTextJobQualifications
        },
        setFieldValue,
    } = props;

    const [expandedPanelId, setExpandedPanelId] = React.useState<string>();
    
    const [qualField, qualMeta, qualHelpers] = useField<JobQualifications>(
        fieldName
    );

    React.useEffect(() => {
        scrollToElementById(expandedPanelId, "desktop");
    } , [expandedPanelId, qualField.value]);

    const setTouched = () => {
        qualHelpers.setTouched(true);
    };

    const handleAddEducationQualification = () => {
        const { educationJobQualifications } = qualField.value;

        const newEduQual: EducationJobQualification = {
            education: {
                educationChoiceId: getPlaceholderIndex(),
                educationChoiceTagId: undefined,
                educationChoiceTagName: undefined,
            },
            id: getPlaceholderIndex(),
            qualification: Qualification.Education,
            isRequired: true,
        };
        qualHelpers.setValue({
            ...qualField.value,
            educationJobQualifications: [
                ...educationJobQualifications,
                newEduQual,
            ],
        });
        setTouched();
        setExpandedPanelId(`educationJobQualification${newEduQual.id}`);
    };

    const handleAddSkillAllOfQualification = () => {
        const { skillJobQualifications } = qualField.value;
        const newSkillQual: SkillJobQualification = {
            id: getPlaceholderIndex(),
            qualification: Qualification.SkillAllOf,
            isRequired: true,
            yearsExperience: YearsExperience.One,
            skills: [],
        };
        qualHelpers.setValue({
            ...qualField.value,
            skillJobQualifications: [...skillJobQualifications, newSkillQual],
        });
        setTouched();
        setExpandedPanelId(`skillJobQualification${newSkillQual.id}`);
    };

    const handleAddLicenseAllOfQualification = () => {
        const { licenseJobQualifications } = qualField.value;
        const newLicenseQual: LicenseJobQualification = {
            id: getPlaceholderIndex(),
            qualification: Qualification.License,
            isRequired: true,
            licenses: [],
        };
        qualHelpers.setValue({
            ...qualField.value,
            licenseJobQualifications: [
                ...licenseJobQualifications,
                newLicenseQual,
            ],
        });
        setTouched();
        setExpandedPanelId(`licenseJobQualification${newLicenseQual.id}`);
    };

    const handleAddCustomTextQualification = () => {
        const { customTextJobQualifications } = qualField.value;
        const newCustomTextQual: CustomTextJobQualification = {
            id: getPlaceholderIndex(),
            qualification: Qualification.CustomText,
            isRequired: false,
            customText: "",
        };
        qualHelpers.setValue({
            ...qualField.value,
            customTextJobQualifications: [
                ...customTextJobQualifications,
                newCustomTextQual,
            ],
        });
        setTouched();
        setExpandedPanelId(`customJobQualification${newCustomTextQual.id}`);
    };


    const handleLicenseQualTypeChange = (
        qualification: ILicenseJobQualification
    ) => {
        const {
            licenseJobQualifications,
            licenseAnyOfJobQualifications,
        } = qualField.value;

        //initialize fields with original state
        let updatedAllOfQuals = licenseJobQualifications;
        let updatedAnyOfQuals = licenseAnyOfJobQualifications;

        if (
            qualification.qualification.isLicense() ||
            qualification.qualification.isLicenseWithState()
        ) {
            updatedAllOfQuals = licenseJobQualifications.concat([
                qualification,
            ]);
            updatedAnyOfQuals = licenseAnyOfJobQualifications.filter(
                x => x.id !== qualification.id
            );
        }

        if (
            qualification.qualification.isLicenseAnyOf() ||
            qualification.qualification.isLicenseWithStateAnyOf()
        ) {
            updatedAnyOfQuals = licenseAnyOfJobQualifications.concat([
                qualification,
            ]);
            updatedAllOfQuals = licenseJobQualifications.filter(
                x => x.id !== qualification.id
            );
        }

        qualHelpers.setValue({
            ...qualField.value,
            licenseJobQualifications: updatedAllOfQuals,
            licenseAnyOfJobQualifications: updatedAnyOfQuals,
        });
        setTouched();
    };

    const handleSkillQualTypeChange = (
        qualification: SkillJobQualification
    ) => {
        const {
            skillJobQualifications,
            skillAnyOfJobQualifications,
        } = qualField.value;

        let updatedAllOfQuals = skillJobQualifications;
        let updatedAnyOfQuals = skillAnyOfJobQualifications;

        if (qualification.qualification.isSkillAllOf()) {
            updatedAllOfQuals = skillJobQualifications.concat([qualification]);
            updatedAnyOfQuals = skillAnyOfJobQualifications.filter(
                x => x.id !== qualification.id
            );
        }

        if (qualification.qualification.isSkillAnyOf()) {
            updatedAnyOfQuals = skillAnyOfJobQualifications.concat([
                qualification,
            ]);
            updatedAllOfQuals = skillJobQualifications.filter(
                x => x.id !== qualification.id
            );
        }

        qualHelpers.setValue({
            ...qualField.value,
            skillJobQualifications: updatedAllOfQuals,
            skillAnyOfJobQualifications: updatedAnyOfQuals,
        });
        setTouched();
    };

    const handleCustomQualTypeChange = (
        qualification: ICustomJobQualification
    ) => {
        const {
            customTextJobQualifications,
            customYesNoJobQualifications,
        } = qualField.value;

        let updatedTextQuals = customTextJobQualifications;
        let updatedYesNoQuals = customYesNoJobQualifications;

        if (qualification.qualification.isCustomText()) {
            updatedTextQuals = customTextJobQualifications.concat([
                qualification,
            ]);
            updatedYesNoQuals = customYesNoJobQualifications.filter(
                x => x.id !== qualification.id
            );
        }

        if (qualification.qualification.isCustomYesNo()) {
            updatedYesNoQuals = customYesNoJobQualifications.concat([
                qualification,
            ]);
            updatedTextQuals = customTextJobQualifications.filter(
                x => x.id !== qualification.id
            );
        }

        qualHelpers.setValue({
            ...qualField.value,
            customTextJobQualifications: updatedTextQuals,
            customYesNoJobQualifications: updatedYesNoQuals,
        });
    };

    const handlePanelChange = (
        panel: React.ReactElement<PanelProps>,
        expanded: boolean
    ) => {
        setExpandedPanelId(expanded ? panel.props.id : null);
    };

    return (
        <>
            <FieldArray name={fieldName}>
                {() => (
                    <ExpansionPanels
                        id="qualificationEditorExpansionPanels"
                        expansionMode="single"
                    >
                        {educationPanels({
                            educationJobQualifications: educationJobQualifications,
                            customClassName: classes.background,
                            fieldName: `${fieldName}.educationJobQualifications`,
                            setFieldValue: setFieldValue,
                            setTouched: setTouched,
                            error: (qualMeta.error as any)
                                ?.educationJobQualifications,
                            onPanelChange: handlePanelChange,
                            expandedPanelId: expandedPanelId,
                        })}
                        {skillAllOfPanels({
                            skillJobQualifications: skillJobQualifications,
                            customClassName: classes.background,
                            fieldName: `${fieldName}.skillJobQualifications`,
                            setFieldValue: setFieldValue,
                            setTouched: setTouched,
                            onSkillQualTypeChange: handleSkillQualTypeChange,
                            getPlaceholderIndex: getPlaceholderIndex,
                            error: (qualMeta.error as any)
                                ?.skillJobQualifications,
                            onPanelChange: handlePanelChange,
                            setExpandedPanelId: setExpandedPanelId,
                            expandedPanelId: expandedPanelId,
                        })}
                        {skillAnyOfPanels({
                            skillAnyOfJobQualifications: skillAnyOfJobQualifications,
                            customClassName: classes.background,
                            fieldName: `${fieldName}.skillAnyOfJobQualifications`,
                            setFieldValue: setFieldValue,
                            setTouched: setTouched,
                            onSkillQualTypeChange: handleSkillQualTypeChange,
                            getPlaceholderIndex: getPlaceholderIndex,
                            error: (qualMeta.error as any)
                                ?.skillAnyOfJobQualifications,
                            onPanelChange: handlePanelChange,
                            setExpandedPanelId: setExpandedPanelId,
                            expandedPanelId: expandedPanelId,
                        })}
                        {licenseAllOfPanels({
                            licenseJobQualifications: licenseJobQualifications,
                            customClassName: classes.background,
                            fieldName: `${fieldName}.licenseJobQualifications`,
                            setFieldValue: setFieldValue,
                            onLicenseQualTypeChange: handleLicenseQualTypeChange,
                            getPlaceholderIndex: getPlaceholderIndex,
                            error: (qualMeta.error as any)
                                ?.licenseJobQualifications,
                            onPanelChange: handlePanelChange,
                            setExpandedPanelId: setExpandedPanelId,
                            expandedPanelId: expandedPanelId,
                        })}
                        {licenseAnyOfPanels({
                            licenseAnyOfJobQualifications: licenseAnyOfJobQualifications,
                            customClassName: classes.background,
                            fieldName: `${fieldName}.licenseAnyOfJobQualifications`,
                            setFieldValue: setFieldValue,
                            onLicenseQualTypeChange: handleLicenseQualTypeChange,
                            getPlaceholderIndex: getPlaceholderIndex,
                            error: (qualMeta.error as any)
                                ?.licenseAnyOfJobQualifications,
                            onPanelChange: handlePanelChange,
                            setExpandedPanelId: setExpandedPanelId,
                            expandedPanelId: expandedPanelId,
                        })}
                        {customYesNoPanels({
                            customYesNoJobQualifications: customYesNoJobQualifications,
                            customClassName: classes.background,
                            fieldName: `${fieldName}.customYesNoJobQualifications`,
                            setFieldValue: setFieldValue,
                            getPlaceholderIndex: getPlaceholderIndex,
                            onCustomQualTypeChange: handleCustomQualTypeChange,
                            error: (qualMeta.error as any)
                                ?.customYesNoJobQualifications,
                            onPanelChange: handlePanelChange,
                            setExpandedPanelId: setExpandedPanelId,
                            expandedPanelId: expandedPanelId,
                        })}
                        {customTextPanels({
                            customTextJobQualifications: customTextJobQualifications,
                            customClassName: classes.background,
                            fieldName: `${fieldName}.customTextJobQualifications`,
                            setFieldValue: setFieldValue,
                            getPlaceholderIndex: getPlaceholderIndex,
                            onCustomQualTypeChange: handleCustomQualTypeChange,
                            error: (qualMeta.error as any)
                                ?.customTextJobQualifications,
                            onPanelChange: handlePanelChange,
                            setExpandedPanelId: setExpandedPanelId,
                            expandedPanelId: expandedPanelId,
                        })}
                    </ExpansionPanels>
                )}
            </FieldArray>
            <div className={classes.addQualsContainer}>
                <div className={classes.addQualsContainerRow}>
                    <Button
                        color="default"
                        size="small"
                        variant="contained"
                        startIcon={<PlusIcon />}
                        onClick={handleAddEducationQualification}
                        aria-label="add education qualification"
                        //disable add edu button if there already is an edu qual in state
                        disabled={
                            qualField.value.educationJobQualifications.length >
                            0
                        }
                    >
                        <span> education</span>
                    </Button>
                    <Button
                        color="default"
                        size="small"
                        variant="contained"
                        startIcon={<PlusIcon />}
                        onClick={handleAddSkillAllOfQualification}
                        aria-label="add skill qualification"
                    >
                        <span> skill</span>
                    </Button>
                    <Button
                        color="default"
                        size="small"
                        variant="contained"
                        startIcon={<PlusIcon />}
                        onClick={handleAddLicenseAllOfQualification}
                        aria-label="add license/certification qualification"
                    >
                        <span> license/certification</span>
                    </Button>
                </div>
                <div className={classes.addQualsContainerRow}>
                    <Button
                        color="default"
                        size="small"
                        variant="contained"
                        startIcon={<PlusIcon />}
                        onClick={handleAddCustomTextQualification}
                        aria-label="add custom question qualification"
                    >
                        <span> custom question</span>
                    </Button>
                </div>
            </div>
        </>
    );
};
